<script setup lang="ts">
import { useStore } from '@/stores/store'
import { storeToRefs } from 'pinia'
import FormBlock from '@/components/SizeAndButtons/FormBlock.vue'
import CushionForm from '@/components/CanvasCushion/CushionForm.vue'
import { IconReload, IconArrowLeft, IconArrowRight } from '@/components/icons'
import { ref, onUpdated, nextTick, useTemplateRef, onMounted, computed } from 'vue'
const store = useStore()
const {
  currentStep,
  diameter_a_circle,
  width_a_rectangle,
  length_b_rectangle,
  thickness,
  cushion,
  isButtons,
  isStitches,
  isButtonMiddle,
  buttonsCols,
  buttonsRows,
  stitchesCols,
  stitchesRows,
  buttonsDistribution,
  buttonsDistanceToEdge,
  stitchesDistribution,
  stitchesDistanceToEdge,
  topStitchesType,
  crossTopSewing,
} = storeToRefs(store)

const showCanvas = computed(() =>
  store.isCircle
    ? Number(diameter_a_circle.value) >= 30
    : Number(width_a_rectangle.value) >= 30 && Number(length_b_rectangle.value) >= 30,
)

const isNotFlat = computed(() => cushion.value !== 'flat_cushion')

const isSizeSetted = computed(() =>
  store.isCircle
    ? Number(diameter_a_circle.value) > 0
    : Number(width_a_rectangle.value) > 0 && Number(length_b_rectangle.value) > 0,
)

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const isAllSizesReady = computed(() =>
  isNotFlat.value ? isSizeSetted.value && Boolean(thickness.value) : isSizeSetted.value,
)

const rightFormWrapperWidth = ref(0)
const rightFormWrapperHeight = ref(0)
const rightFormWrapper = useTemplateRef('rightFormWrapper')
const isInvalid = computed(() => {
  if (
    store.isCircle &&
    diameter_a_circle.value &&
    Number(diameter_a_circle.value) >= 30 &&
    Number(diameter_a_circle.value) <= 200 &&
    isButtons.value &&
    isStitches.value
  ) {
    if (isButtons.value == 'buttons_no' && isStitches.value == 'stitches_no') {
      return false
    }
    if (
      isButtons.value == 'buttons_yes' &&
      isButtonMiddle.value &&
      isStitches.value == 'stitches_no'
    ) {
      return false
    }
    if (
      isButtons.value == 'buttons_no' &&
      isStitches.value == 'stitches_yes' &&
      crossTopSewing.value &&
      topStitchesType.value
    ) {
      return false
    }
    if (
      isButtons.value == 'buttons_yes' &&
      isStitches.value == 'stitches_yes' &&
      crossTopSewing.value &&
      topStitchesType.value
    ) {
      return false
    }
  }
  if (
    !store.isCircle &&
    isButtons.value &&
    isStitches.value &&
    width_a_rectangle.value &&
    length_b_rectangle.value &&
    Number(width_a_rectangle.value) >= 30 &&
    Number(width_a_rectangle.value) <= 240 &&
    Number(length_b_rectangle.value) >= 30 &&
    Number(length_b_rectangle.value) <= 240
  ) {
    if (isButtons.value == 'buttons_no' && isStitches.value == 'stitches_no') {
      return false
    }
    if (
      isButtons.value == 'buttons_yes' &&
      buttonsDistribution.value &&
      isStitches.value == 'stitches_no' &&
      buttonsCols.value &&
      buttonsRows.value &&
      Number(buttonsCols.value) * Number(buttonsRows.value) < 20
    ) {
      if (
        buttonsDistribution.value == 'distribution_fixed_distance' &&
        buttonsDistanceToEdge.value
      ) {
        return false
      }
      if (buttonsDistribution.value == 'distribution_simple_split') {
        return false
      }
    }
    if (
      isButtons.value == 'buttons_no' &&
      isStitches.value == 'stitches_yes' &&
      stitchesCols.value &&
      stitchesRows.value &&
      Number(stitchesRows.value) * Number(stitchesCols.value) < 20 &&
      topStitchesType.value
    ) {
      if (
        stitchesDistribution.value == 'stitches_distribution_fixed_distance' &&
        stitchesDistanceToEdge.value
      ) {
        return false
      }
      if (stitchesDistribution.value == 'stitches_distribution_simple_split') {
        return false
      }
    }
    if (
      isButtons.value == 'buttons_yes' &&
      isStitches.value == 'stitches_yes' &&
      buttonsCols.value &&
      buttonsRows.value &&
      Number(buttonsCols.value) * Number(buttonsRows.value) < 20 &&
      topStitchesType.value
    ) {
      if (
        buttonsDistribution.value == 'distribution_fixed_distance' &&
        buttonsDistanceToEdge.value
      ) {
        return false
      }
      if (buttonsDistribution.value == 'distribution_simple_split') {
        return false
      }
    }
  }
  return true
})

function makeOrderInfo() {
  // need validation with vee-validate
  if (!isInvalid.value) {
    store.currentStep = 4
    document.querySelector('.configurator_right_column')?.scrollIntoView({ behavior: 'smooth' })
  }
}

onUpdated(() => {
  nextTick(() => {
    if (rightFormWrapper.value) {
      rightFormWrapperWidth.value = rightFormWrapper.value?.offsetWidth || 0
      rightFormWrapperHeight.value = rightFormWrapper.value?.offsetHeight || 0
    }
  })
})
onMounted(() => {
  rightFormWrapperWidth.value = rightFormWrapper.value?.offsetWidth || 0
  rightFormWrapperHeight.value = rightFormWrapper.value?.offsetHeight || 0
})

function toBack() {
  currentStep.value = 2
  document.querySelector('.configurator_right_column')?.scrollIntoView({ behavior: 'smooth' })
}
</script>

<template>
  <div v-if="currentStep === 3">
    <div class="left-form-wrapper flex gap-10">
      <FormBlock />
      <div class="rightFormWrapper" style="height: auto; min-width: 200px">
        <div
          ref="rightFormWrapper"
          style="height: 200px; min-width: 200px; position: sticky; top: 15px"
        >
          <CushionForm
            v-if="showCanvas"
            :parent-width="rightFormWrapperWidth"
            :parent-height="rightFormWrapperHeight"
          />
        </div>
      </div>
    </div>

    <div class="flex flex-col lg:flex-row gap-4 justify-between mt-6 lg:mt-10">
      <div class="flex gap-2">
        <button type="button" class="btn btn-primary" @click="toBack">
          <IconArrowLeft />
          <span>{{ $t('Back') }}</span>
        </button>
        <button type="button" :disabled="isInvalid" class="btn btn-primary" @click="makeOrderInfo">
          <span>{{ $t('Next') }}</span>
          <IconArrowRight />
        </button>
      </div>
      <button type="button" class="btn" @click="store.nullSize">
        <IconReload />
        <span>{{ $t('Drop off') }}</span>
      </button>
    </div>
  </div>
</template>
<style>
.rightFormWrapper {
  display: none;
}
@media (min-width: 1024px) {
  .rightFormWrapper {
    display: block;
  }
}
</style>
